import { DESCRIPTION_PLACEHOLDER_KEY } from '@/common/constants/constants';
import { useContentEdit } from '@/common/hooks/useContentEdit';
import { Textarea, TextareaProps } from '@mantine/core';
import React, { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface ContentEditItem {
    id: string;
    content: string;
    update_id?: string;
}

export interface ContentEditTextareaProps<T extends ContentEditItem> {
    item: T;
    updateFunction: (id: string, data: Partial<T>) => Promise<T>;
    onContentChange?: (content: string) => void;
    placeholder?: string;
    debounceTime?: number;
    textareaProps?: Omit<
        TextareaProps,
        'value' | 'onChange' | 'placeholder' | 'ref'
    >;
    leftSection?: ReactNode;
    leftSectionProps?: React.ComponentPropsWithoutRef<'div'>;
    autoSave?: boolean;
}

export function ContentEditTextarea<T extends ContentEditItem>({
    item,
    updateFunction,
    onContentChange,
    placeholder,
    debounceTime = 1000,
    textareaProps,
    leftSection,
    leftSectionProps,
    autoSave = true,
}: ContentEditTextareaProps<T>) {
    const { t } = useTranslation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const cursorPositionRef = useRef<number | null>(null);
    const currentContentRef = useRef<string>(item?.content || '');

    const [content, setContent] = useContentEdit<T>(
        item,
        autoSave ? updateFunction : null,
        debounceTime
    );

    // 수동 저장 로직
    const saveContent = () => {
        if (
            !autoSave &&
            item?.id &&
            currentContentRef.current !== item.content
        ) {
            void updateFunction(item.id, {
                content: currentContentRef.current,
            } as Partial<T>);
        }
    };

    const handleContentChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        // 커서 위치 저장
        cursorPositionRef.current = event.currentTarget.selectionStart;

        const newContent = event.currentTarget.value;
        currentContentRef.current = newContent;
        setContent(newContent);

        // 상위 컴포넌트에 변경된 내용 전달
        if (onContentChange) {
            onContentChange(newContent);
        }
    };

    // 텍스트 업데이트 후 커서 위치 복원
    React.useEffect(() => {
        if (textareaRef.current && cursorPositionRef.current !== null) {
            textareaRef.current.selectionStart = cursorPositionRef.current;
            textareaRef.current.selectionEnd = cursorPositionRef.current;
        }
    }, [content]);

    return (
        <Textarea
            placeholder={placeholder ?? t(DESCRIPTION_PLACEHOLDER_KEY)}
            value={content}
            onChange={handleContentChange}
            ref={textareaRef}
            leftSection={leftSection}
            leftSectionProps={leftSectionProps}
            onBlur={() => {
                if (!autoSave) {
                    saveContent();
                }
            }}
            onKeyDown={e => {
                if (!autoSave && (e.key === 'Enter' || e.key === 'Escape')) {
                    saveContent();
                }
            }}
            {...textareaProps}
        />
    );
}
